// Ayuda contextual del módulo Sistemas (administración). Sistemas → Resumen → Ayuda.
// Patrón ayuda-de-modulo: checklist en vivo + flujo + ¿cómo hago...? + glosario.
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, CheckCircle2, ChevronDown, ChevronRight, LifeBuoy, Link2, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { apiClient } from '@/lib/api/client';
import { RIESGO_BADGE } from './shared';
import type { RiesgoAsistente } from './shared';

interface AccionAsistente {
  clave: string;
  nombre: string;
  descripcion: string;
  riesgo: RiesgoAsistente;
  habilitada: boolean;
}

interface AyudaData {
  checklist: Record<string, boolean>;
  kpis: { empresas: number; usuarios: number; perfiles: number; opciones_asignadas: number };
  asistente?: { acciones: AccionAsistente[]; dispositivos: number };
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'empresas', label: 'Empresa(s) creada(s)', ruta: '/seguridad/empresas', titulo: 'Empresas', ayuda: 'Cada empresa es un tenant aislado: sus datos no se mezclan con los de otras.' },
  { key: 'perfiles', label: 'Perfiles definidos', ruta: '/seguridad/perfiles', titulo: 'Perfil', ayuda: 'El perfil agrupa permisos por rol (Administrador nivel 5, Cajero, Contador…).' },
  { key: 'usuarios', label: 'Usuarios creados', ruta: '/seguridad/usuarios', titulo: 'Usuarios', ayuda: 'Cada persona con su empresa y perfil asignados.' },
  { key: 'asignaciones', label: 'Menú asignado a los perfiles (esta empresa)', ruta: 'permission-assignment', titulo: 'Asignación Menú x Perfil', ayuda: 'Sin asignación, el usuario entra pero NO ve las opciones del menú.' },
  { key: 'worker_sri', label: 'Worker SRI conectado', ruta: '/sistemas/procesos', titulo: 'Monitor de procesos', ayuda: 'Descarga y emite comprobantes al SRI (lo activa TI vía systemd).', advisory: true },
  { key: 'worker_notificador', label: 'Worker Notificador conectado', ruta: '/sistemas/procesos', titulo: 'Monitor de procesos', ayuda: 'Envía correos/WhatsApp (recordatorios, roles de pago, contratos).', advisory: true },
  { key: 'worker_asistente', label: 'Worker Asistente conectado', ruta: '/sistemas/arquitectura', titulo: 'Arquitectura del sistema', ayuda: 'El agente de chat/voz del ERP (lo activa TI vía systemd; necesita la API key de Anthropic en asistente/.env).', advisory: true },
  { key: 'asistente_acciones', label: 'Acciones del asistente habilitadas (esta empresa)', ruta: '/sistemas/asistente', titulo: 'Asistente — Configuración', ayuda: 'Sin habilitación por perfil, el asistente no puede ejecutar NADA para esa persona.', advisory: true },
  { key: 'asistente_dispositivos', label: 'Dispositivos vinculados al asistente (esta empresa)', ruta: '/sistemas/asistente', titulo: 'Asistente — Configuración', ayuda: 'Cada nº de WhatsApp o dispositivo debe vincularse a UN usuario; un número desconocido no recibe respuesta.', advisory: true },
];

const FLUJO = [
  { n: 1, titulo: 'Crear la empresa', detalle: 'El tenant: nombre, RUC, moneda. Todo lo demás vive dentro de ella.', ruta: '/seguridad/empresas', tituloTab: 'Empresas' },
  { n: 2, titulo: 'Definir perfiles', detalle: 'Roles con nivel de acceso (5 = administrador; el menú Sistemas exige nivel 5).', ruta: '/seguridad/perfiles', tituloTab: 'Perfil' },
  { n: 3, titulo: 'Crear usuarios', detalle: 'Cada usuario pertenece a una empresa y tiene un perfil.', ruta: '/seguridad/usuarios', tituloTab: 'Usuarios' },
  { n: 4, titulo: 'Asignar el menú al perfil', detalle: 'Marca qué opciones ve cada perfil y con qué permisos — usa el marcado MASIVO por menú/submenú para hacerlo en segundos.', ruta: 'permission-assignment', tituloTab: 'Asignación Menú x Perfil' },
  { n: 5, titulo: 'El usuario entra', detalle: 'Al iniciar sesión ve solo su menú. Si cambiaste permisos, debe salir y volver a entrar.', ruta: '/seguridad/usuarios', tituloTab: 'Usuarios' },
  { n: 6, titulo: 'Monitorear', detalle: 'Procesos (workers), arquitectura del sistema y la base de datos.', ruta: '/sistemas/arquitectura', tituloTab: 'Arquitectura del sistema' },
  { n: 7, titulo: 'Habilitar el asistente (opcional)', detalle: 'Decide qué ACCIONES puede pedir cada perfil y vincula los números de WhatsApp de los usuarios. Sin habilitación, el asistente conversa pero no ejecuta nada.', ruta: '/sistemas/asistente', tituloTab: 'Asistente — Configuración' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo encuentro una opción sin recorrer todo el menú? (Ctrl+K)',
    pasos: [
      'Pulsa Ctrl+K (⌘+K en Mac) en cualquier pantalla, o haz clic en la caja de búsqueda de la barra superior. Se abre el buscador de opciones.',
      'Escribe parte del nombre y muévete con ↑ ↓; Enter la abre en una pestaña nueva, Esc cierra el buscador.',
      'No hace falta poner tildes ni respetar el orden de las palabras: "nomina" encuentra Nómina, y "cont asien" encuentra Contabilidad › Asientos igual que "asien cont".',
      'Busca también por lo que la opción HACE, no sólo por su nombre: escribir "cronológico" lleva al Libro Diario aunque esa palabra no esté en el título.',
      'Cada resultado muestra debajo su módulo y submenú. Eso importa porque hay nombres que se repiten: 14 opciones se llaman "Ayuda" y 9 "Dashboard"; lo que las distingue es dónde viven.',
      'Con la caja vacía te ofrece las últimas opciones que abriste desde el buscador, guardadas por empresa en este navegador.',
      'Sólo aparecen opciones a las que tu perfil tiene acceso: el buscador lee el mismo menú que ves en la barra lateral, así que no puede mostrarte nada que no puedas abrir.',
    ],
    enlaces: [{ to: '/seguridad/opciones', t: 'Opciones' }, { to: 'permission-assignment', t: 'Asignación Menú x Perfil' }],
  },
  {
    q: '¿Cómo comparto el enlace de acceso de una empresa?',
    pasos: [
      'Ve a Empresas y, en la fila de la empresa, toca el ícono de enlace ("Enlace de acceso").',
      'Ahí ves la URL de acceso (con botón Copiar) y el código QR (con Descargar).',
      'Ese enlace lleva al login ya personalizado con el nombre y logo de esa empresa.',
      'Compártelo por correo/WhatsApp o imprime el QR (carné, cartelera).',
    ],
    enlaces: [{ to: '/seguridad/empresas', t: 'Empresas' }],
  },
  {
    q: '¿Cómo personalizo la pantalla de login de una empresa?',
    pasos: [
      'En Empresas, edita la empresa: el campo "Fondo de login" permite subir una imagen (se optimiza sola en el navegador).',
      'También se usan el logo y el nombre comercial de la empresa en esa pantalla.',
      'Se ve al abrir el enlace de acceso de esa empresa (/login/su-enlace), con un velo para que el formulario siga legible.',
    ],
    enlaces: [{ to: '/seguridad/empresas', t: 'Empresas' }],
  },
  {
    q: '¿Cómo cambia su contraseña un usuario?',
    pasos: [
      'Cualquier usuario logueado: menú de usuario (arriba a la derecha, sobre el avatar) → "Cambiar mi contraseña".',
      'Completa clave actual + nueva (mínimo 8 caracteres) + confirmar.',
      'Además, cuando un administrador o RRHH resetea un acceso, el usuario queda obligado a cambiar la clave en su primer ingreso.',
    ],
    enlaces: [{ to: '/seguridad/usuarios', t: 'Usuarios' }],
  },
  {
    q: '¿Cómo entra cada empresa al sistema?',
    pasos: [
      'Cada empresa tiene su propio enlace de acceso (no hay un selector de empresas por privacidad).',
      'El usuario entra con su usuario y contraseña de ESA empresa.',
      'El mismo nombre de usuario (p. ej. "supervisor") puede existir en varias empresas como cuentas separadas e independientes.',
    ],
    enlaces: [{ to: '/seguridad/empresas', t: 'Empresas' }, { to: '/seguridad/usuarios', t: 'Usuarios' }],
  },
  {
    q: '¿Cómo doy acceso a un usuario nuevo?',
    pasos: [
      'Créalo en Usuarios con su empresa y perfil.',
      'Verifica que su PERFIL tenga el menú asignado (Asignación Menú x Perfil) — si el perfil ya estaba configurado, no hay nada más que hacer.',
      'El usuario inicia sesión y ve su menú. Si no ve una opción, casi siempre es asignación faltante o falta re-login.',
    ],
    enlaces: [{ to: '/seguridad/usuarios', t: 'Usuarios' }, { to: 'permission-assignment', t: 'Asignación Menú x Perfil' }],
  },
  {
    q: '¿Cómo asigno permisos de forma MASIVA (menú o submenú completo)?',
    pasos: [
      'En Asignación Menú x Perfil elige la empresa y el perfil.',
      'Cada MENÚ tiene el checkbox "Todo el menú" y cada SUBMENÚ el checkbox "Todo": un clic marca o desmarca todas sus opciones.',
      'Qué permisos aplica: los "permisos globales" que tengas seleccionados arriba (crear/editar/eliminar/xls/pdf); si no seleccionaste ninguno, aplica TODOS.',
      'Truco: selecciona solo "xls, pdf" en globales y marca el menú Reportes completo → perfil de solo exportación.',
      'El checkbox muestra un guion (—) cuando el grupo está parcialmente marcado.',
      'Afina opción por opción si hace falta y pulsa GUARDAR una sola vez al final.',
      'El menú Sistemas solo puede asignarse a perfiles de nivel 5 (bloqueado para el resto).',
    ],
    enlaces: [{ to: 'permission-assignment', t: 'Asignación Menú x Perfil' }],
  },
  {
    q: 'Un usuario no ve una opción del menú — ¿por qué?',
    pasos: [
      'La opción existe pero su PERFIL no la tiene asignada en esa EMPRESA → asígnala y guarda.',
      'El usuario debe CERRAR SESIÓN y volver a entrar (el menú se carga al iniciar sesión).',
      'Si el menú/submenú completo no aparece: un menú sin al menos una opción con permiso se oculta entero.',
    ],
    enlaces: [{ to: 'permission-assignment', t: 'Asignación Menú x Perfil' }],
  },
  {
    q: '¿Cómo creo o reorganizo menús, submenús y opciones?',
    pasos: [
      'Menú → SubMenú → Opciones (en ese orden): cada opción lleva la RUTA de su pantalla y un ícono.',
      'Después de crear una opción, asígnala a los perfiles — sin asignación nadie la ve.',
      'El orden numérico controla la posición en la barra lateral.',
    ],
    enlaces: [{ to: '/seguridad/menus', t: 'Menú' }, { to: '/seguridad/submenus', t: 'SubMenú' }, { to: '/seguridad/opciones', t: 'Opciones' }],
  },
  {
    q: '¿Dónde veo la arquitectura y el estado del sistema?',
    pasos: [
      'Arquitectura del sistema: tecnología, diagramas, mapa de la base de datos y módulos — con números EN VIVO.',
      'Monitor de procesos: el avance de los workers (descarga SRI, emisión).',
      'Gestión Base de Datos: consola SQL para consultas puntuales (con cuidado).',
    ],
    enlaces: [{ to: '/sistemas/arquitectura', t: 'Arquitectura del sistema' }, { to: '/sistemas/procesos', t: 'Monitor de procesos' }],
  },
  {
    q: '¿Qué es el asistente del ERP y cómo funciona?',
    pasos: [
      'Es un agente de chat/voz (WhatsApp y web) que ejecuta acciones del ERP COMO EL USUARIO que escribe: usa una sesión propia de ese usuario, así que sus permisos y su empresa aplican igual que si entrara por pantalla.',
      'Solo puede pedir las ACCIONES del catálogo (tabla de esta página) que estén habilitadas para su PERFIL en su EMPRESA — sin habilitación, la acción no existe para él.',
      'Cada acción tiene un NIVEL DE RIESGO: consulta (responde directo), operativa (resume lo que va a hacer y espera tu "sí"), crítica (además exige una advertencia explícita; en Fase 2, biometría/PIN).',
      'Toda la conversación y cada acción ejecutada quedan auditadas en la base de datos (quién, qué, cuándo y con qué resultado).',
      'Si un número de WhatsApp no está vinculado a un usuario, el asistente no le responde información del ERP.',
    ],
    enlaces: [{ to: '/sistemas/arquitectura', t: 'Arquitectura del sistema' }],
  },
  {
    q: '¿Cómo configuro el asistente para mi empresa?',
    pasos: [
      'TI arranca el worker (carpeta asistente/, servicio systemd): necesita la API key de Anthropic y la conexión a la base en asistente/.env. El check "Worker Asistente conectado" de arriba se pone verde cuando está en línea.',
      'En Sistemas → Asistente → Configuración, habilita las acciones POR PERFIL (matriz acción × perfil): qué puede pedir un Cajero no es lo mismo que un Administrador. Regla de oro: empieza solo con consultas y agrega operativas/críticas perfil por perfil. Los administradores (nivel 5) vienen con todo habilitado de fábrica.',
      'En la misma pantalla, vincula los DISPOSITIVOS: cada nº de WhatsApp (+5939…) se asocia a UN usuario de esta empresa. Es la identidad del canal — trátala como una credencial.',
      'Para WhatsApp, TI apunta el webhook de Twilio al worker (/webhook/twilio).',
      'Prueba con una consulta inocua ("¿cuánto vendimos hoy?") antes de habilitar acciones operativas.',
    ],
    enlaces: [{ to: '/sistemas/asistente', t: 'Asistente — Configuración' }, { to: '/seguridad/perfiles', t: 'Perfil' }, { to: '/seguridad/usuarios', t: 'Usuarios' }],
  },
  {
    q: 'El asistente no responde o dice que no puede hacer algo — ¿por qué?',
    pasos: [
      '"Este número no está vinculado…": falta registrar el dispositivo en Sistemas → Asistente → Configuración (canal + identificador → usuario). Verifica también que el usuario esté ACTIVO.',
      'Conversa pero "no conoce" una acción: la acción no está habilitada para el PERFIL del usuario en esa EMPRESA (o está inactiva en el catálogo) — revísalo en la matriz de la pantalla de configuración.',
      'Pidió confirmación y quedó esperando: responde "sí" para ejecutar o "no"/"cancela" para descartar — la acción pendiente vive en la conversación.',
      'No responde nada: revisa que el worker esté conectado (check de arriba) y que tenga la API key configurada.',
    ],
    enlaces: [{ to: '/sistemas/asistente', t: 'Asistente — Configuración' }, { to: '/seguridad/usuarios', t: 'Usuarios' }],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Tenant (empresa)', d: 'Unidad de aislamiento de datos: cada empresa solo ve lo suyo, garantizado por Row-Level Security en la base de datos.' },
  { t: 'Perfil / nivel de acceso', d: 'Rol del usuario. El nivel 5 es administrador: puede configurar, resolver diferencias y ver el menú Sistemas.' },
  { t: 'Asignación Menú x Perfil', d: 'La matriz que define qué opciones ve cada perfil en cada empresa, y con qué permisos (crear/editar/eliminar/xls/pdf).' },
  { t: 'Marcado masivo', d: 'Checkbox "Todo el menú"/"Todo" en las cabeceras: aplica los permisos globales seleccionados (o todos) a todas las opciones del grupo de un clic.' },
  { t: 'Permisos globales', d: 'La selección de permisos de la parte superior de la pantalla de asignación: es lo que aplican los marcados masivos.' },
  { t: 'Worker', d: 'Proceso de fondo (SRI, notificador, asistente, trading) que corre en el servidor y se coordina con la base de datos; se monitorea en Procesos.' },
  { t: 'Asistente del ERP', d: 'Agente de chat/voz (WhatsApp/web) que ejecuta acciones del sistema como el usuario que escribe, con sus mismos permisos y empresa.' },
  { t: 'Acción del asistente', d: 'Cada cosa que el asistente sabe hacer (catálogo global). Se habilita por empresa + perfil: sin habilitación, no existe para ese usuario.' },
  { t: 'Nivel de riesgo', d: 'Clasificación de cada acción: consulta (ejecuta directo), operativa (pide confirmación verbal), crítica (confirmación reforzada y advertencia).' },
  { t: 'Dispositivo vinculado', d: 'El nº de WhatsApp (o dispositivo) asociado a un usuario: es su identidad ante el asistente. Un canal no vinculado no recibe datos del ERP.' },
  { t: 'Enlace de acceso (slug)', d: 'Identificador propio de cada empresa que va en la URL de su login (p. ej. /login/miempresa-xxxx). Se genera automáticamente al crear la empresa, no se adivina, y sirve para mostrar la marca correcta antes de iniciar sesión.' },
  { t: 'Usuario por empresa', d: 'El nombre de usuario y el correo son únicos por empresa (no globales): una misma persona puede tener cuentas separadas en distintas empresas.' },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña "Instalador Tauri": documentación del despliegue ON-PREMISE con app
// de escritorio — arquitectura aprobada (2026-09-09) cuando un cliente exige
// instalador único y/o su backend corre en un equipo de SU red local.
function AyudaTauri() {
  const S = ({ children }: { children: ReactNode }) => <span className="font-medium">{children}</span>;
  const C = ({ children }: { children: ReactNode }) => <code className="rounded bg-muted px-1 text-xs font-mono">{children}</code>;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Qué es y cuándo aplica</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            El <S>instalador Tauri</S> convierte el ERP en una <S>aplicación de escritorio instalable</S> (Windows/Linux, ~5-10 MB):
            misma interfaz web de siempre — <S>no es otro sistema</S> — envuelta en un ejecutable con icono propio, modo kiosko
            nativo y el <S>puente de hardware embebido</S> (impresoras, balanza, display, cajón) sin instalar el print-agent aparte.
          </p>
          <p>Aplica en dos escenarios:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><S>Cliente nube + instalador</S>: el backend sigue en nuestros servidores; la app solo reemplaza al navegador en las cajas.</li>
            <li><S>Cliente ON-PREMISE</S> (el caso que activó esta fase): el <S>backend completo corre en un equipo del propio cliente</S>,
              dentro de su red local — funciona sin internet y los datos no salen de su sitio.</li>
          </ul>
          <p className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
            <S>Estado</S>: el cascarón está CONSTRUIDO en <C>erp-desktop/</C> del repositorio (puente de hardware embebido
            validado contra el protocolo real del print-agent). Falta compilar el primer instalador en una PC Windows —
            pasos exactos en <C>erp-desktop/README.md</C> (Rust + VS Build Tools + <C>npm run build</C> → instalador NSIS).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Arquitectura on-premise</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs font-mono leading-relaxed">{`  RED LOCAL DEL CLIENTE (sin dependencia de internet)
  ┌─────────────────────────────────────────────────────────┐
  │  SERVIDOR LOCAL (un equipo dedicado del cliente)        │
  │   · PostgreSQL (esquemas del ERP + datos de SU empresa) │
  │   · PostgREST  :3999  (la API real)                     │
  │   · nginx      :80/443 (sirve el frontend compilado)    │
  │   · workers según módulos: sri-worker (facturación),    │
  │     print-agent, notificador, respaldos-worker          │
  └───────────────▲─────────────────▲───────────────────────┘
                  │                 │
        ┌─────────┴──────┐  ┌───────┴────────┐
        │ CAJA 1         │  │ CAJA 2 / admin │   + tablets por
        │ app TAURI      │  │ app TAURI o    │     navegador
        │ (kiosko + HW)  │  │ navegador      │     (misma red)
        └────────────────┘  └────────────────┘`}</pre>
          <ul className="list-disc ml-5 space-y-1">
            <li>La <S>facturación SRI</S> sí necesita salida a internet (solo el sri-worker hacia el SRI); todo lo demás opera en LAN.</li>
            <li>El servidor local se instala con las <S>mismas piezas del repositorio</S>: scripts SQL en orden (<C>deploy/db-deploy.sh</C>,
              que mantiene el ledger local del cliente), <C>postgrest.conf</C> desde su .example, unidades systemd de <C>deploy/</C>.</li>
            <li>Los datos del cliente nacen con la <S>receta de empresa nueva</S> (o migración desde su sistema anterior, como se hizo con OISHI).</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Qué trae la app Tauri (por qué no es solo un navegador)</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <ul className="list-disc ml-5 space-y-1">
            <li><S>Misma SPA</S>: cero reescritura — el ejecutable carga la interfaz desde el servidor (local o nube), así las
              actualizaciones de pantallas siguen siendo instantáneas sin reinstalar.</li>
            <li><S>Kiosko nativo</S>: pantalla completa real, sin barra del navegador ni banderas de Chrome; arranque directo al POS.</li>
            <li><S>Puente de hardware embebido</S>: impresión silenciosa (red y USB), balanza serial, display cliente y cajón —
              el mismo código del print-agent, integrado (sin servicio aparte en cada caja).</li>
            <li><S>Configuración local</S>: la primera ejecución (o el acceso directo con <C>--config</C>) abre la página de
              configuración: URL del servidor, kiosko y periféricos.</li>
            <li><S>Auto-update del cascarón</S>: al arrancar consulta <C>{'{servidor}'}/desktop/latest.json</C>; si hay versión
              nueva la descarga, verifica la <S>firma</S> y se reinstala sola — con 140 locales, publicar una versión es un solo
              deploy (proceso en <C>erp-desktop/README.md</C>, script <C>publicar.mjs</C>). La SPA ni siquiera necesita esto:
              se carga fresca del servidor en cada arranque.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Proceso de implementación en un cliente on-premise</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          {[
            ['1 · Dimensionar y preparar el servidor', 'Equipo dedicado (mín. 4 núcleos, 8 GB RAM, SSD 256 GB, UPS) con Linux. IP FIJA en la red del local (ej. 192.168.1.10) — todas las cajas apuntan a ella.'],
            ['2 · Instalar el backend', 'PostgreSQL + PostgREST + nginx con las piezas del repo: scripts SQL en orden con db-deploy.sh (deja ledger propio para futuras actualizaciones), roles y JWT secret nuevos para el cliente, workers según módulos contratados (sri-worker si factura, print-agent, notificador, respaldos-worker con destino externo).'],
            ['3 · Sembrar la empresa', 'Receta de empresa nueva (asignaciones de menú, plan contable, seeds POS) o migración de su sistema anterior. Certificado .p12 y secuenciales si factura electrónicamente.'],
            ['4 · Compilar el frontend para SU servidor', 'Build con la URL de su API local (mismo esquema del build remoto: DEPLOY_BASE y VITE_POSTGREST_URL apuntando al server del cliente, nombre/marca según contrato).'],
            ['5 · Generar el instalador Tauri', 'Empaquetado por sistema operativo apuntando a la IP/host del servidor local, con el puente de hardware embebido y el canal de auto-update sirviéndose del mismo nginx.'],
            ['6 · Instalar las cajas', 'Ejecutar el instalador en cada PC de caja (siguiente-siguiente), configurar hardware (impresoras por estación, balanza COM, display) desde las pantallas del ERP como siempre.'],
            ['7 · Respaldos y contingencia', 'respaldos-worker con copia FUERA del servidor (disco externo o SSH a otro equipo) + prueba de restauración. El POS además conserva su modo offline por caja.'],
            ['8 · Actualizaciones del cliente', 'Cada release: rsync de scripts SQL nuevos + db-deploy.sh apply (el ledger dice qué falta) + dist nuevo al nginx local. Las cajas se actualizan solas al recargar; el cascarón Tauri solo cuando cambie el puente.'],
          ].map(([t, d], i) => (
            <div key={t} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{i + 1}</div>
              <div><span className="font-medium">{t.slice(4)}.</span> <span className="text-muted-foreground">{d}</span></div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Decisiones ya tomadas (no re-discutir)</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <ul className="list-disc ml-5 space-y-1">
            <li><S>Tauri y no Electron</S>: ejecutable ~5 MB vs ~150 MB, menos superficie de ataque, mismo resultado.</li>
            <li><S>La SPA se carga desde el servidor</S> (no incrustada en el ejecutable): conserva el deploy instantáneo — la razón
              por la que se descartó un “POS desktop” separado.</li>
            <li><S>Un solo codebase</S>: cualquier pantalla nueva sirve igual a clientes nube, on-premise y con instalador.</li>
            <li>El <S>puente de hardware</S> es el mismo del print-agent (WebSocket local): el POS no distingue si habla con el
              agente instalado o con el embebido en Tauri.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

export function AyudaSistemas() {
  const ir = useIr();
  const [tab, setTab] = useState<'guia' | 'tauri'>('guia');
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['sys-ayuda-estado'],
    queryFn: async () => (await apiClient.post('/rpc/sys_ayuda_estado', {})).data,
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LifeBuoy className="w-5 h-5" /> Ayuda — Sistemas
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      <div className="flex items-center gap-2 border-b">
        {([['guia', 'Guía del módulo'], ['tauri', 'Instalador Tauri']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'tauri' && <AyudaTauri />}
      {tab === 'guia' && (<>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = data?.checklist?.[c.key] ?? false;
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                  : <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.advisory ? 'text-amber-500' : 'text-red-500'}`} />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {data && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              {data.kpis.empresas} empresa(s) · {data.kpis.usuarios} usuario(s) · {data.kpis.perfiles} perfil(es) ·
              {' '}{data.kpis.opciones_asignadas} opciones de menú asignadas en esta empresa
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo de la administración</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="w-4 h-4" /> Asistente del ERP — acciones disponibles (en vivo)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            El asistente conversa por WhatsApp o web y ejecuta estas acciones <span className="font-medium text-foreground">como el usuario que escribe</span> (sus
            permisos y su empresa aplican solos). Las de riesgo <span className="font-medium">consulta</span> responden directo; las <span className="font-medium">operativas</span> resumen
            lo que van a hacer y esperan un «sí»; las <span className="font-medium">críticas</span> exigen confirmación reforzada. Todo queda auditado.
          </p>
          <div className="space-y-1.5">
            {(data?.asistente?.acciones ?? []).map((a) => (
              <div key={a.clave} className="flex items-start gap-2 text-sm">
                <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 mt-0.5 ${RIESGO_BADGE[a.riesgo].cls}`}>
                  {RIESGO_BADGE[a.riesgo].label}
                </span>
                <div className={a.habilitada ? '' : 'opacity-60'}>
                  <span className="font-medium">{a.nombre}</span>
                  <span className="text-muted-foreground"> — {a.descripcion}</span>
                  {!a.habilitada && <span className="text-amber-600 dark:text-amber-400"> (sin habilitar en esta empresa)</span>}
                </div>
              </div>
            ))}
            {data && !data.asistente?.acciones?.length && (
              <div className="text-sm text-muted-foreground">El catálogo de acciones aún no está instalado (script 311).</div>
            )}
          </div>
          {data?.asistente && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              {data.asistente.dispositivos} dispositivo(s) vinculados en esta empresa · la habilitación es por perfil: ver «¿Cómo configuro el asistente?» abajo.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal ml-5 space-y-1">
                {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              <div className="flex flex-wrap gap-3 mt-2">
                {c.enlaces.map((e) => <Ir key={e.to} to={e.to} t={e.t} />)}
              </div>
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm">
              <span className="font-medium">{g.t}:</span>{' '}
              <span className="text-muted-foreground">{g.d}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      </>)}
    </div>
  );
}
